import React, { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router'
import ReactDatePicker from 'react-datepicker'
import moment from 'moment-timezone'
import { Badge, Button, Icon, Input, Label, TanTable, TanTableColumnDef, Text, useUI } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { SessionService } from '@evertel/session/SessionService'
import { APIDataAgreementAcceptance } from '@evertel/types'
import { AgreementsLogController } from '@evertel/agreements'

const AgreementsLog: React.FC = observer(() => {
    const sessionService = useService(SessionService, [])
    const departmentId = sessionService.selectedDepartmentId
    const agreementsLogController = useService(AgreementsLogController, [departmentId])
    const { addToast } = useUI()

    const [search, setSearch] = useState('')
    const [startDate, setStartDate] = useState<Date | null>(null)
    const [endDate, setEndDate] = useState<Date | null>(null)
    const [isExporting, setIsExporting] = useState(false)

    // The pickers show local wall-clock digits that the user reads as UTC (labels say
    // "(UTC)"), so the ceiling is "now, in UTC wall-clock digits": take the current UTC
    // time and reinterpret those same fields as local, giving a Date the picker renders
    // as the current UTC time. Prevents picking a future UTC instant.
    const maxDate = moment(moment.utc().format('YYYY-MM-DDTHH:mm:ss')).toDate()

    useEffect(() => {
        if (!departmentId) return
        agreementsLogController.init(departmentId)
        agreementsLogController.search()
    }, [departmentId])

    const handleSearchChange = (value: string) => {
        setSearch(value)
        agreementsLogController.setSearchTerm(value)
        agreementsLogController.debouncedSearch()
    }

    const handleStartDateChange = (date: Date | null) => {
        setStartDate(date)
        agreementsLogController.setStartDate(date)
        agreementsLogController.search()
    }

    const handleEndDateChange = (date: Date | null) => {
        setEndDate(date)
        agreementsLogController.setEndDate(date)
        agreementsLogController.search()
    }

    const handleExportCSV = async () => {
        setIsExporting(true)
        try {
            await agreementsLogController.exportCSV()
        } catch (e: any) {
            addToast({ color: 'danger', title: 'Export Failed', message: e?.message || 'Failed to export the log. Please try again.' })
        } finally {
            setIsExporting(false)
        }
    }

    const columns = useMemo<TanTableColumnDef<APIDataAgreementAcceptance>[]>(() => [
        {
            id: 'agreementName',
            header: 'Agreement',
            size: '25%',
            cell: ({ row }) => row.original.departmentAgreement?.title ?? '—'
        },
        {
            id: 'userName',
            header: 'User',
            size: '15%',
            cell: ({ row }) => {
                const user = row.original.acceptedBy
                if (!user) return '—'
                const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || '—'
                return (
                    <Link className="btn btn-link p-0" to={`/manage/agency/${departmentId}/user/${user.id}`}>
                        {`${name} (ID: #${user.id})`}
                    </Link>
                )
            }
        },
        {
            id: 'userEmail',
            header: 'Email',
            size: '20%',
            cell: ({ row }) => row.original.acceptedBy?.email ?? '—'
        },
        {
            id: 'roomName',
            header: 'Room',
            size: '15%',
            cell: ({ row }) => {
                const { room, roomId } = row.original
                if (room?.name) return <span>{`${room.name} (ID: #${room.id})`}</span>
                if (roomId) return <span>{`(ID: #${roomId})`}</span>
                return '—'
            }
        },
        {
            accessorKey: 'createdDate',
            header: 'Accepted At (UTC)',
            size: 170,
            enableSorting: true,
            cell: ({ getValue }) => getValue()
                ? moment(getValue() as string).utc().format('MM/DD/YYYY HH:mm')
                : '—'
        },
        {
            id: 'version',
            header: 'Version',
            size: 75,
            cell: ({ row }) => {
                const version = row.original.agreementDocument?.version
                if (!version) return '—'
                const isLatest = row.original.agreementDocumentId === row.original.departmentAgreement?.agreementDocumentId
                return (
                    <Badge
                        color={isLatest ? 'primary' : 'secondary'}
                        variant={isLatest ? 'soft' : 'solid'}
                        style={{ fontSize: '0.7rem' }}
                    >
                        v{version}
                    </Badge>
                )
            }
        }
    ], [departmentId])

    return (
        <>
            <strong className="d-block">Agreements Log</strong>
            <span style={{ fontSize: '0.85rem' }} className="d-block mb-3 text-muted">
                View who accepted which version and when.
            </span>
            <div className="border rounded p-3 mb-3 d-flex flex-row flex-wrap align-items-end gap-2">
                <div className="d-flex flex-column" style={{ minWidth: 200, flex: 1 }}>
                    <Label
                        text="Search Logs"
                        htmlFor="log-search"
                    />
                    <Input
                        id="log-search"
                        name="log-search"
                        type="text"
                        placeholder="Search by user or agreement"
                        value={search}
                        onChange={(e) => handleSearchChange(e.target.value)}
                    />
                </div>
                <div className="d-flex flex-column">
                    <Label
                        text="Start Date (UTC)"
                        htmlFor="log-start-date"
                    />
                    <ReactDatePicker
                        id="log-start-date"
                        selected={startDate}
                        onChange={handleStartDateChange}
                        placeholderText="Select date"
                        className="form-control"
                        calendarClassName="datepicker"
                        isClearable={!!startDate}
                        selectsStart
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="MM/dd/yyyy HH:mm"
                        startDate={startDate}
                        endDate={endDate}
                        maxDate={endDate ?? maxDate}
                    />
                </div>
                <div className="d-flex flex-column">
                    <Label
                        text="End Date (UTC)"
                        htmlFor="log-end-date"
                    />
                    <ReactDatePicker
                        id="log-end-date"
                        selected={endDate}
                        onChange={handleEndDateChange}
                        placeholderText="Select date"
                        className="form-control"
                        calendarClassName="datepicker"
                        isClearable={!!endDate}
                        selectsEnd
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="MM/dd/yyyy HH:mm"
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate ?? undefined}
                        maxDate={maxDate}
                    />
                </div>
                <div style={{ flex: 1 }} />
                <Button
                    color="primary"
                    onClick={handleExportCSV}
                    loading={isExporting}
                    disabled={isExporting}
                >
                    <Icon name="download" className="me-2" />
                    Export CSV
                </Button>
            </div>
            <TanTable
                style={{ fontSize: '0.85rem' }}
                data={agreementsLogController.acceptances}
                columns={columns}
                loading={agreementsLogController.isLoading}
                remote={true}
                rowCount={agreementsLogController.acceptancesCount}
                options={{
                    responsive: true,
                    responsiveHeight: 'calc(100vh - 280px)',
                    pageSize: agreementsLogController.limit,
                    pageNumber: agreementsLogController.page,
                    showPagination: agreementsLogController.acceptancesCount > 25,
                    showTotal: true,
                    truncateCells: true,
                    onPageChange: (page) => {
                        agreementsLogController.setPage(page)
                        agreementsLogController.search()
                    },
                    onPageSizeChange: (pageSize) => {
                        agreementsLogController.setLimit(pageSize)
                        agreementsLogController.search()
                    },
                    sortName: agreementsLogController.sortBy,
                    sortOrder: agreementsLogController.sortOrder,
                    onSortChange: (name, order) => {
                        if (!order) return
                        agreementsLogController.setSort(name, order)
                        agreementsLogController.search()
                    },
                    emptyState: (
                        <div className="text-center py-1">
                            <Text color="muted">There are no current records</Text>
                        </div>
                    )
                }}
            />
        </>
    )
})

AgreementsLog.displayName = 'AgreementsLog'
export { AgreementsLog }
